import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { LOGIN_USER, LOGIN_STUDENT } from "../graphql/mutation";
import { useSession } from "../context/SessionContext";
import CustomSnackbar from "../components/Snackbar";
import {
  LoginUserResponse,
  LoginStudentResponse,
  LoginUserVariables,
  SnackbarType,
} from "../interfaces/auth.interface";
import { MESSAGES } from "../utils/const";
import themestyle from "../theme";
import Copyright from "../components/Copyright";
import { REGEX } from "../utils/const";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useSession();
  const apollo = useApolloClient();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarType>("error");

  const [loginUser, { loading: userLoading }] = useMutation<
    LoginUserResponse,
    LoginUserVariables
  >(LOGIN_USER);
  const [loginStudent, { loading: studentLoading }] = useMutation<
    LoginStudentResponse,
    LoginUserVariables
  >(LOGIN_STUDENT);
  const loading = userLoading || studentLoading;

  useEffect(() => {
    document.title = "Login | United Grooves Admin Panel";
  }, []);

  const validateEmail = () => {
    if (!email) {
      setEmailError(MESSAGES.EMAIL_REQUIRED);
      return false;
    }
    if (!REGEX.EMAIL.test(email)) {
      setEmailError(MESSAGES.EMAIL_VALID);
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = () => {
    if (!password) {
      setPasswordError(MESSAGES.PASS_REQUIRED);
      return false;
    }
    if (password.length < 8) {
      setPasswordError(MESSAGES.PASSWORD_MUST_CHAR);
      return false;
    }
    setPasswordError("");
    return true;
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  // Persist the session (token + user) and enter the app. Shared by both the
  // student and admin/instructor login paths so behaviour is identical.
  const enterSession = async (result: {
    token: string | null;
    user: unknown;
    message: string;
  }) => {
    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberMe");
    }
    // Persist in localStorage (new tabs) + sessionStorage (current tab).
    if (result.token) {
      localStorage.setItem("token", result.token);
      sessionStorage.setItem("token", result.token);
    }
    localStorage.setItem("user", JSON.stringify(result.user));
    sessionStorage.setItem("user", JSON.stringify(result.user));
    await apollo.clearStore();
    login();
    setSnackbarMessage(result.message);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setTimeout(() => navigate("/dashboard"), 1200);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const isEmailValid = validateEmail();
    const isPasswordValid = validatePassword();
    if (!isEmailValid || !isPasswordValid) return;
    try {
      // 1) Student path first — this allows a system auto-inactive student to
      //    self-return and returns the correct block message for archived/deleted/
      //    admin-deactivated accounts (loginUser alone blocks all isActive=false).
      const { data: sData } = await loginStudent({
        variables: { email, password },
      });
      const student = sData?.loginStudent;
      if (student?.success && student.token) {
        await enterSession(student);
        return;
      }

      // 2) Fallback — admin/instructor portal. loginStudent returns no token for
      //    non-USER roles, so those accounts authenticate here.
      const { data: uData } = await loginUser({
        variables: { email, password },
      });
      const user = uData?.loginUser;
      if (user?.success && user.token) {
        await enterSession(user);
        return;
      }

      // Both paths failed — prefer the student message (more specific for lifecycle
      // blocks like archived/deleted/deactivated), then the admin message, then generic.
      setSnackbarMessage(
        student?.message || user?.message || MESSAGES.INVALID_CREDENTIALS,
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage(MESSAGES.INVALID_CREDENTIALS);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const isFormValid =
    email.length > 0 &&
    password.length >= 8 &&
    emailError === "" &&
    passwordError === "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 4, sm: 3, md: 6 },
          bgcolor: themestyle.colors.white,
          position: "relative",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.2,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src="united-grooves-logo.png"
              alt="United Grooves"
              sx={{ width: 22, height: 31 }}
            />
            <Typography
              sx={{
                fontFamily: themestyle.fontFamily.Roboto,
                fontWeight: 500,
                fontSize: "16px",
                letterSpacing: "1px",
                mt: "3px",
                ml: "4px",
              }}
            >
              {MESSAGES.BRAND_NAME}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: themestyle.fontFamily.Roboto,
              fontWeight: 500,
              fontSize: { xs: "30px", sm: "2.2rem", md: "2.5rem" },
              mb: 1,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.WELCOME_BACK}
          </Typography>
          <Typography
            sx={{
              color: themestyle.colors.textSecondary,
              fontFamily: themestyle.fontFamily.Roboto,
              fontSize: { xs: "14px", sm: "16px", md: "16px" },
              mb: 4,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.LOGIN_SUBTITLE}
          </Typography>
          <form onSubmit={handleLogin}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  mb: 0.8,
                  fontSize: { xs: "10px", sm: "0.9rem" },
                  fontWeight: 600,
                }}
              >
                {MESSAGES.EMAIL_LABEL}
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder={MESSAGES.EMAIL_PLACEHOLDER}
                value={email}
                onChange={handleEmailChange}
                onBlur={validateEmail}
                error={!!emailError}
                helperText={emailError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: { xs: "9px", sm: "0.75rem" },
                    marginTop: "4px",
                    marginLeft: "2px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: { xs: "40px", sm: "46px" },
                    "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themestyle.colors.activeGreen,
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ mb: 1.5, mt: 3 }}>
              <Typography
                sx={{
                  mb: 0.8,
                  fontSize: { xs: "10px", sm: "0.9rem" },
                  fontWeight: 600,
                }}
              >
                {MESSAGES.PASSWORD_LABEL}
              </Typography>
              <TextField
                fullWidth
                placeholder={MESSAGES.PASSWORD_PLACEHOLDER}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                onBlur={validatePassword}
                error={!!passwordError}
                helperText={passwordError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: { xs: "8px", sm: "0.75rem" },
                    marginTop: "4px",
                    marginLeft: "2px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: { xs: "40px", sm: "46px" },
                    "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themestyle.colors.activeGreen,
                      borderWidth: "1px",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        size="small"
                        sx={{
                          "& svg": { fontSize: { xs: 18, sm: 20, md: 24 } },
                        }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      padding: "4px",
                      ml: "6px",
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                      color: "gray",
                      "&.Mui-checked": { color: themestyle.colors.activeGreen },
                      "&:hover": {
                        backgroundColor: "rgba(108, 188, 68, 0.08)",
                      },
                    }}
                  />
                }
                label="Remember me"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "10px", sm: "13px", md: "13px" },
                    fontFamily: themestyle.fontFamily.Roboto,
                  },
                }}
              />

              <Link
                href="/forgotpassword"
                underline="none"
                sx={{
                  fontFamily: themestyle.fontFamily.Roboto,
                  color: themestyle.colors.primary,
                  fontSize: { xs: "10px", sm: "13px", md: "13px" },
                  fontWeight: 400,
                  "&:hover": {
                    color: themestyle.colors.primary,
                    textDecoration: "underline",
                  },
                }}
              >
                {MESSAGES.FORGOT_PASSWORD}
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              disabled={!isFormValid || loading}
              sx={{
                width: "100%",
                mt: { xs: 0.4, md: 0.8 },
                backgroundColor: themestyle.colors.activeGreen,
                color: themestyle.colors.white,
                fontSize: { xs: "11px", sm: "16px" },
                py: 1.2,
                height: { xs: "38px", sm: "auto" },
                textTransform: "none",
                borderRadius: "5px",
                "&.Mui-disabled": {
                  backgroundColor: "#9E9E9E",
                  color: themestyle.colors.white,
                },
                "&:hover": {
                  backgroundColor: themestyle.colors.secondary,
                },
              }}
            >
              {MESSAGES.SIGN_IN}
            </Button>
            <Box
              sx={{
                mt: { xs: 1.2, sm: 1.8, md: 2.3 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: themestyle.fontFamily.Roboto,
                  fontSize: { xs: "10px", sm: "16px", md: "14px" },
                  color: themestyle.colors.textSecondary,
                  fontWeight: 400,
                }}
              >
                {MESSAGES.DONT_HAVE_ACCOUNT}{" "}
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "10px", sm: "16px", md: "14px" },
                    fontFamily: themestyle.fontFamily.Roboto,
                    color: themestyle.colors.activeGreen,
                    fontWeight: 400,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => navigate("/signup")}
                >
                  {MESSAGES.SIGN_UP}
                </Typography>
              </Typography>
            </Box>
          </form>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Copyright />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          bgcolor: themestyle.colors.black,
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Copyright />
        <Box textAlign="center">
          <Box
            component="img"
            src="united-grooves-logo.png"
            alt="United Grooves"
            sx={{ width: { xs: 80, md: 200 }, mb: 2 }}
          />
          <Typography
            sx={{
              color: themestyle.colors.white,
              fontSize: { xs: 18, md: 40 },
              letterSpacing: { xs: "2px", md: "12px" },
            }}
          >
            {MESSAGES.BRAND_NAME}
          </Typography>
        </Box>
      </Box>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
export default Login;
